import { Component, OnDestroy, OnInit } from '@angular/core';
import { OrderItem, OrderItemList, OrderService } from 'com.trumbull.phx.order.api-client';
import { VocabularyItemList } from 'com.trumbull.phx.vocabulary.api-client';
import { TableLazyLoadEvent } from 'primeng/table';
import { catchError, finalize, of, Subscription } from 'rxjs';
import { OrderGridFilterComponent } from './order-grid-filter/order-grid-filter.component';
import { SearchParams, TableParams } from './search-params.model';
import { OrderGridComponent } from './order-grid/order-grid.component';
import { FilterTypes } from './order-grid-filter/order-grid-filter.model';
import { IPSAuthenticationService, IPSTenantKeyService } from '@ips/common-angular';
import { UserDefinition, UserService } from 'com.trumbull.phx.user.api-client';
import { SharedService } from '../services/shared.service';
import { VocabHelperService } from '../services/vocab-helper/vocab-helper.service';
import { UserHelperService } from '../services/user-helper/user-helper.service';

@Component({
  selector: 'app-search-results',
  imports: [
    OrderGridFilterComponent,
    OrderGridComponent
  ],
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss'
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  orders: OrderItem[] = [];
  orderVocab: VocabularyItemList[] = [];
  loading: boolean = false;
  totalRecords: number = 0;
  error: string | null = null;
  defaultOrderOwner: any;
  externalUser: string = '';
  users: UserDefinition[] = [];
  orderOwnerList:  UserDefinition[] = [];
  subscriptions: Subscription = new Subscription();
  hasLoadedInitialData: boolean = false;
  private currentParams: SearchParams = {
    pageNumber: 1,
    pageSize: 10,
    number: []
  };
  selectedTenant?: string | null;

  tableParams:TableParams = {
    sortField: 'number',
    sortOrder: -1,
    first: 0, // For nth page (pageIndex = (n-1) → (n-1) * 10 = 10(n-1))
    rowsPerPage: 10,
    orderOwner: [],
    orderType: [],
    accountName: [],
    createdBy: [],
    clientName: []
  }

  ordersListFilter: FilterTypes = {
    orderOwner: [],
    accountName: [],
    createdBy: [],
    orderType: [],
    status: [],
    clientName: []
  };
      
  constructor(
    private readonly orderService: OrderService,
    private readonly authz: IPSAuthenticationService,
    private readonly userService: UserHelperService,
    private readonly sharedService: SharedService,
    private vocabHelperService: VocabHelperService,
    private readonly tenantKeyService: IPSTenantKeyService,
  ) {}

  ngOnInit() {
    this.vocabHelperService.getVocabData(['OrderType', 'RecordTypes', 'RecordRetrievalCustomerCode', 'OrderStatusCode', 'RecordRetrievalRequestStatusCode']).subscribe(res => {
      this.orderVocab = res;
    });
  //  this.orders = OrdersList; // FIXME: local dev
    this.subscribeToSearchChange();
    this.setBreadcrumbPath();
  }

getUsersList(
  accountNames: string[] = [],
  attributeKey: string | undefined = undefined,
  callback: (users: any[]) => void
): void {
  // ✅ Sort the array to ensure consistent cache key
  const attributeValue = accountNames.length ? [...accountNames].sort().join(',') : undefined;

  this.userService
    .getUsersByAttribute(attributeKey, attributeValue)
    .subscribe({
      next: (users) => {
        callback(users || []);
      },
      error: (error) => {
        console.error(error);
      },
    });
}

  getAccountNames(accountNames: any) {
    this.fetchOrderByUsersList(accountNames, 'organization');
  }

  fetchUsers(accountNames: string[] = [], attributeKey: string | undefined = undefined): void {
    this.getUsersList(accountNames, attributeKey, (users) => {
      this.users = users;
    });
  }
  
  fetchOrderByUsersList(accountNames: string[]  = [], attributeKey: string | undefined = undefined): void {
    this.getUsersList(accountNames, attributeKey, (users) => {
      this.orderOwnerList = users;
    });
  }

  loadOrders(event: TableLazyLoadEvent, loggedInUser: string | undefined) {
    this.orders = [];
    this.hasLoadedInitialData = true;
    this.loading = true; 
    this.error = null;

    this.currentParams = {
      ...this.currentParams,
      pageNumber: event.first ? Math.floor(event.first / (event.rows ?? 10)) + 1 : 1,
      pageSize: event.rows ?? 10,
      sort: event.sortField ? 
        `${event.sortOrder === 1 ? '+' : '-'}${event.sortField}` : 
        this.currentParams.sort
    };

    this.orderService.listOrders(
      'standard',
      this.currentParams.id?.join(''),   // id
      this.currentParams.number?.join(''),   // number
      this.currentParams.status?.join(''),   // status
      this.currentParams.orderBy?.join(','),   // by
      this.currentParams.account?.join(','),   // account
      this.currentParams.customer?.join(','), // customer
	    '',
      this.currentParams.ref?.join(''),   // ref
      this.currentParams.from?.join(''),   // from
      this.currentParams.to?.join(''),   // to
      this.currentParams.createdBy?.join(','), // createdBy
      this.currentParams.orderType?.length ? 'orderType:'+this.currentParams.orderType?.join(',') : '', //orderType
      this.currentParams.pageNumber,
      this.currentParams.pageSize,
      this.currentParams.sort
    )
    .pipe(
      catchError(error => {
        this.error = 'Failed to load orders. Please try again later.';
        console.error('Error loading orders:', error);
        return of({ items: [], total: 0 });
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe(
      (response) => {
        this.orders = (response as OrderItemList).orders ?? [];
        this.totalRecords = (response as OrderItemList).count ?? 0;
        if (this.totalRecords === 0 && loggedInUser) {
          this.currentParams = {
            ...this.currentParams,
            orderBy: []
          } 
          this.loadOrders({ first: 1, rows: 10, sortField: 'number', sortOrder: 0 }, '');
        }
  
        if (loggedInUser && this.totalRecords > 0 && this.externalUser) {
          this.defaultOrderOwner = loggedInUser;
        }
      }
    );
  }

  onFilterChange(filters: FilterTypes) {
    const selectedFilter = { ...filters };
    this.currentParams = {
      ...this.currentParams,
      orderBy: selectedFilter.orderOwner,
      orderType: selectedFilter.orderType,
      account: selectedFilter.accountName,
      createdBy: selectedFilter.createdBy,
      customer: selectedFilter.clientName
    };
    if (!this.hasLoadedInitialData) {
      this.setSelectedOrderGridParams();
    }
    this.loadOrders({
      first: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.first : 0,
      rows: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.rowsPerPage : this.currentParams.pageSize,
      sortField: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.sortField : undefined,
      sortOrder: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.sortOrder : undefined
    }, undefined);
  }

  subscribeToSearchChange() {
    this.loading = true; 
    const subscription$ = this.sharedService.orderSearchTerm$.subscribe(orderId => {
      if (orderId)
        this.subscribeToCurrentUserChange(orderId);
      else
        this.subscribeToCurrentUserChange(null);
    });
    this.subscriptions.add(subscription$);
  }

  subscribeToCurrentUserChange(orderId: string | null) {
    const subscription$ = this.authz.currentUser.subscribe((user) => { 
      this.orders = [];
      this.tenantKeyService.getTenantKey().subscribe((selectedTenant) => {
        this.selectedTenant = selectedTenant;
      });
      const loggedInUser = user?.definition?.principalName ? user?.definition?.principalName : '';
      const organizationValue = user?.definition?.attributes?.find(item => item?.key === 'organization' && item?.tenantKey?.toLowerCase() === this.selectedTenant?.toLowerCase());
      this.externalUser = organizationValue?.value || '';
      if (organizationValue?.value) {
        this.fetchUsers([organizationValue?.value], 'organization');
      } else {
        this.fetchUsers();
      }
      this.currentParams = {
        ...this.currentParams,
        ...( this.externalUser && { 
            orderBy: [loggedInUser],
          })
      }  
      if (orderId) {
        this.currentParams.number = [];
        this.currentParams.number.push(orderId);
        this.loadOrders({ first: 1, rows: 10, sortField: 'number', sortOrder: 0 }, loggedInUser );
      } else {
        delete this.currentParams.number;
        if (this.externalUser) {
          if (!this.hasLoadedInitialData) {
            this.setSelectedOrderGridParams();
          }
          this.loadOrders({
            first: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.first : 1,
            rows: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.rowsPerPage : this.currentParams.pageSize,
            sortField: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.sortField : 'number',
            sortOrder: !this.hasLoadedInitialData ? this.sharedService.OrderGridParams.sortOrder : 0
          }, loggedInUser);
        }
          // this.loadOrders({ first: 1, rows: 10, sortField: 'number', sortOrder: 0 }, loggedInUser );
      }
    }); 
    this.subscriptions.add(subscription$);
  }

  setBreadcrumbPath() {
    this.sharedService.updateBreadcrumbPath([
      { label: 'Orders List' }
    ]);
  }
  
  setSelectedOrderGridParams() {
    this.tableParams.first = this.sharedService.OrderGridParams.first;
    this.tableParams.rowsPerPage = this.sharedService.OrderGridParams.rowsPerPage;
    this.tableParams.sortField = this.sharedService.OrderGridParams.sortField;
    this.tableParams.sortOrder = this.sharedService.OrderGridParams.sortOrder;
    if (this.sharedService.OrderGridParams.updatedFilterValues) {
      this.ordersListFilter.accountName = this.sharedService.OrderGridParams.accountName; 
      this.ordersListFilter.createdBy = this.sharedService.OrderGridParams.createdBy;
      this.ordersListFilter.orderOwner = this.sharedService.OrderGridParams.orderOwner;
      this.ordersListFilter.orderType = this.sharedService.OrderGridParams.orderType;
      this.ordersListFilter.clientName = this.sharedService.OrderGridParams.clientName;
      this.currentParams.orderBy = this.sharedService.OrderGridParams.orderOwner;
      this.currentParams.orderType = this.sharedService.OrderGridParams.orderType;
      this.currentParams.account = this.sharedService.OrderGridParams.accountName;
      this.currentParams.createdBy = this.sharedService.OrderGridParams.createdBy;
      this.currentParams.customer = this.sharedService.OrderGridParams.clientName;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
